import { CopyContent, CopyDetail, CopyLocale } from '@/services/lifecycleAPI';

export const FIELD_ORDER = [
  'sms',
  'push_title',
  'push_body',
  'subject_a',
  'subject_b',
  'preview',
  'headline',
  'body',
  'button',
] as const;

export const FIELD_LABEL: Record<string, string> = {
  sms: 'SMS',
  push_title: 'Push title',
  push_body: 'Push body',
  subject_a: 'Email subject A',
  subject_b: 'Email subject B',
  preview: 'Email preview text',
  headline: 'Email headline',
  body: 'Email body',
  button: 'Email button',
};

export const SAMPLE_VALUES: Record<string, string> = {
  first_name: 'Maria',
  pct: '60%',
  step_name: 'your profile photo',
  steps_left: '3',
  remaining_steps_list: '☐ your video interview\n☐ your I-9 form',
  days_on_current_step: '4',
  city: 'Miami',
  open_shifts_nearby: '8',
  top_pay_rate: '$22.50',
  radius: '15',
  lifetime_shifts: '3',
  hours_worked: '22.5',
  businesses_count: '2',
  business_name: 'Hotel Colonnade',
  earnings_to_date: '$486.00',
  reason_text: 'face not fully visible',
  eta: '1 to 3',
  needed_item: 'a clearer photo of your ID',
  date: 'Fri, Oct 10',
  time: '9:00 AM',
  amount: '$120.00',
  address: '180 Aragon Ave, Coral Gables',
  link: 'https://m.gravywork.com/x7Kp2Lq9',
};

export const FIELD_LIMITS: Record<string, number> = {
  push_title: 40,
  push_body: 110,
  subject_a: 45,
  subject_b: 45,
  preview: 90,
};

export type VariantText = Record<string, string>;
export type MessageText = Record<string, VariantText>;

export type SampleValue = string | string[];

// A list value (remaining_steps_list) is one "☐ item" per line, as graphy's
// Renderer.format_value writes it. Joined with commas, the preview and the
// email word count disagreed with the server lint (bug hunt 22).
const formatValue = (value: SampleValue): string =>
  Array.isArray(value) ? value.map((item) => `☐ ${item}`).join('\n') : value;

export const interpolate = (
  text: string,
  values: Record<string, SampleValue>,
): string =>
  text.replace(/%\{(\w+)\}/g, (all, key) =>
    values[key] !== undefined ? formatValue(values[key]) : all,
  );

export const variantNames = (detail: CopyDetail): string[] => {
  const names = Object.keys(detail.editable_fields);
  return names.length > 0 ? names : Object.keys(detail.current);
};

export const editableFieldsOf = (
  detail: CopyDetail,
  variant: string,
): string[] => {
  const editable = detail.editable_fields[variant] ?? [];
  return FIELD_ORDER.filter((field) => editable.includes(field));
};

export const requiresOf = (detail: CopyDetail, variant: string): string[] => {
  const requires = detail.current[variant]?.requires;
  return Array.isArray(requires) ? requires.map(String) : [];
};

export const baseText = (detail: CopyDetail): MessageText => {
  const result: MessageText = {};
  variantNames(detail).forEach((variant) => {
    result[variant] = {};
    editableFieldsOf(detail, variant).forEach((field) => {
      const draft = detail.draft?.content?.[variant]?.[field];
      const current = detail.current[variant]?.[field];
      result[variant][field] =
        typeof draft === 'string'
          ? draft
          : typeof current === 'string'
            ? current
            : '';
    });
  });
  return result;
};

export const fullContent = (
  text: MessageText,
  locale: CopyLocale = 'en',
): CopyContent => {
  const content: CopyContent = {};
  Object.entries(text).forEach(([variant, fields]) => {
    const kept = Object.entries(fields).filter(
      ([, value]) => locale === 'en' || value.trim() !== '',
    );
    if (locale === 'en' || kept.length > 0) {
      content[variant] = Object.fromEntries(kept);
    }
  });
  return content;
};

export const isEmptySpanish = (detail: CopyDetail): boolean =>
  detail.locale === 'es' &&
  !detail.draft &&
  Object.values(baseText(detail)).every((fields) =>
    Object.values(fields).every((value) => value.trim() === ''),
  );

export const englishStart = (detail: CopyDetail): MessageText => {
  const result: MessageText = {};
  variantNames(detail).forEach((variant) => {
    result[variant] = {};
    editableFieldsOf(detail, variant).forEach((field) => {
      const value = detail.english?.[variant]?.[field];
      result[variant][field] = typeof value === 'string' ? value : '';
    });
  });
  return result;
};

export const straightenQuotes = (raw: string): string =>
  raw.replace(/[‘’]/g, "'").replace(/[“”]/g, '"');
